/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import {
  Component,
  Input,
  Output,
  ViewChild,
  ViewChildren,
  EventEmitter,
  QueryList,
  OnInit,
  AfterViewInit, OnDestroy,
} from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  Workflow,
  WorkflowService,
  FileType,
  OutputType,
  Parameter,
  CloudRepo,
  Service,
  Item,
} from '../../services/workflow.service';
import {
  AutomatedWorkflow,
  AutomatedWorkflowService,
  EmailOptions,
  EmailFieldMatchMode,
} from '../../services/automated-workflow.service';
import { NgForm } from '@angular/forms';
import { BasicAlertComponent } from '../alerts/basic-alert.component';
import { WorkflowHelperService } from '../../services/workflow-helper.service';
import { XdaStringsService } from '../../services/xda-strings.service';
import { XdaTranslate } from '../../pipes/xda-translate.pipe';
import { UserService } from '../../services/user.service';
import { EmailService } from '../../services/email.service';
import { XrxSettingComponent } from '../controls/xrx-setting.component';
import { XaccService, MWCloudItem, MWCloudRepo } from '../../xacc/xacc.service';
import { XaccBrowseModalComponent } from '../../xacc/xacc-browse-modal.component';
import {
  InputSource,
  EmailSendCondition,
  SettingType,
} from '../../../common/enums';
import { Subscription } from 'rxjs';
import { Icon, IconSize } from '../../../common/iconSettings';
import { Time } from '../controls/xrx-time.component';
import { CloudDestination } from '../../../common/constants';
import { AccessListService } from '../../services/access-list.service';
import { Constants } from '../../../common/constants';

@Component({
  selector: 'automated-workflow-modal-component',
  templateUrl: './automated-workflow-modal.component.html',
  styleUrls: ['./automated-workflow-modal.component.less'],
  standalone: false,
})
export class AutomatedWorkflowModalComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('f', { static: false }) form: NgForm;
  @ViewChildren(XrxSettingComponent)
  serviceParamsQL: QueryList<XrxSettingComponent>;

  private _workflow: AutomatedWorkflow = {} as AutomatedWorkflow;
  private _cloudRepos: CloudDestination[] = [];
  public workflowWarning: boolean = false;

  @Input() workflows: AutomatedWorkflow[] = [];
  @Input() set workflow(workflow: AutomatedWorkflow) {
    this.isNew = false; // edit mode

    // get all services for the workflow
    let services: Service[] = [];
    // deal with cached service objects
    if (workflow.services && typeof workflow.services[0] === 'string') {
      workflow.services.forEach((x: any) =>
        services.push(
          this.workflowService.getSqdService(x, workflow.type === 1),
        ),
      );
      workflow.services = services;
    } else {
      services = workflow.services as Service[];
    }

    // Make a deep copy of workflow
    this._workflow = JSON.parse(JSON.stringify(workflow));

    this.onFirstService = true;

    if (this._workflow.services?.length == 1) {
      this.onLastService = true;
    }

    this.originalServices = JSON.parse(JSON.stringify(this._workflow.services));
    this.availableFileOutputs =
      services[services.length - 1].availableFileOutputs;
    this.lastService = services[services.length - 1];
    this.isTranslateWorkflow = this.lastService.name
      .toLowerCase()
      .includes('translat');

    this.setInput();
    this.setConditions();
    this.setServiceSettings();
    this.setupOutputOnLoad(this.availableFileOutputs);
    this.setupOutputCloudRepoOptions();

    this.originalIcon = JSON.parse(JSON.stringify(this._workflow.icon));
    this.iconOption = this.originalIcon?.glyph
      ? this.iconOptions[1]
      : this.iconOptions[0];
    this.originalName = this.workflowName = this._workflow.name;
    this.originalEnabled = this.isEnabled = this._workflow.enabled;

    // get the workflow this automated workflow is based from.
    this.selectedWorkflow =
      this.workflows.find((x) => x.id == this._workflow.parentId) ?? null;
  }

  get workflow(): AutomatedWorkflow {
    return this._workflow;
  }

  @Input() set cloudRepos(value: CloudDestination[]) {
    this._cloudRepos = value;
    if (value.length) {
      this.inputSources[0].options = value;
      this.outputCloudRepos = JSON.parse(JSON.stringify(value));
    } else {
      // remove the cloud repo option if there aren't any
      this.inputSources.splice(0, 1);
    }
  }
  get cloudRepos(): CloudDestination[] {
    return this._cloudRepos;
  }
  @Output() update = new EventEmitter<any>();

  inputSources = [
    {
      key: 'SDE_CLOUD_REPOSITORIES',
      value: 'SDE_CLOUD_REPOSITORIES',
      title: 'SDE_CLOUD_REPOSITORIES',
      options: this._cloudRepos,
    },
  ];
  inputSourceOptions: any = [
    {
      label: 'SDE_FOLDER',
      value: 'SDE_CLOUD_REPO',
    },
    {
      label: 'SDE_EMAIL2',
      value: 'SDE_EMAIL2',
    },
  ];

  pageRangeOptions: any = [
    {
      label: 'SDE_ALL',
      value: 'SDE_ALL',
    },
    {
      label: 'SDE_SELECT_PAGES',
      value: 'SDE_SELECT_PAGES',
    },
  ];
  pageRangeOption: any = this.pageRangeOptions[0];

  outputCloudRepos: CloudDestination[] = [];

  serviceParams: any[] = [];
  onFirstService = false;
  onLastService = false;
  inputCloudRepoIsInvalid = false;
  outputCloudRepoIsInvalid = false;
  cloudRepoSub: Subscription;
  xaccServiceLoginSub: Subscription;
  selectInputRepo: boolean;
  selectedInputCloudRepo: CloudDestination | null;
  selectedOutputCloudRepo: CloudDestination | null;
  selectedInputCloudFolder: MWCloudItem | null;
  selectedOutputCloudFolder: MWCloudItem | null;
  googlePolicyStr = '';
  allowedEmailDomains: string;
  showResetServiceSettings = true;

  translateFileTypes: any[] = [
    { isChecked: true, value: 'Auto', display: 'SDE_AUTO_SELECT' },
    { isChecked: false, value: 'Pdf', display: 'SDE_PDF1' },
  ];
  selectedTranslateFileType: any = this.translateFileTypes[0];

  originalServices: any;
  serviceSettings: CombinedServiceSettings[] = [];
  originalServiceSettings: CombinedServiceSettings[];
  originalName = '';
  originalEnabled = true;
  lastService: any;
  isTranslateWorkflow = false;
  availableFileOutputs: number;
  servicePageIndex = 0;
  isNew = true;
  savingWorkflow = false;

  steps: Step[] = [
    {
      index: 0,
      name: 'input',
      label: 'SDE_E2_INPUT',
      glyph: 'xgl-input',
      mode: StepMode.Edit,
      status: StepStatus.Default,
    },
    {
      index: 1,
      name: 'conditions',
      label: 'SDE_CONDITIONS',
      glyph: 'xgl-decision',
      mode: StepMode.Disabled,
      status: StepStatus.Default,
    },
    {
      index: 2,
      name: 'workflow',
      label: 'SDE_WORKFLOW',
      glyph: 'xgl-applications',
      mode: StepMode.Disabled,
      status: StepStatus.Default,
    },
    {
      index: 3,
      name: 'output',
      label: 'SDE_OUTPUT',
      glyph: 'xgl-output',
      mode: StepMode.Disabled,
      status: StepStatus.Default,
      hidden: false,
    },
    {
      index: 4,
      name: 'name',
      label: 'SDE_NAME_AND_ICON',
      glyph: 'xgl-rename',
      mode: StepMode.Disabled,
      status: StepStatus.Default,
    },
  ];
  step: Step = this.steps[0];
  getStep(stepName: string) {
    return this.steps.find((x) => x.name === stepName);
  }
  StepStatus = StepStatus;
  StepMode = StepMode;

  xsSummaryCollapsed = true;
  isGoodToGo = true;
  inputOptions: any[];
  inputSource: any;
  errorInput: string;
  errorConditions: string;
  errorName: string;
  errorEmail: string;
  ignorePageRange = false;

  supportedFileTypes = 'DOC, DOCX, PDF, PPT, PPTX, TIFF, XLSX';

  input: any = {
    cloudRepos: [], // type CloudDestination[]
    currentCloudRepo: {},
    isCloudInput: true,
    sendInput: false,
    sendCloudInput: false,
    fileTypes: {
      raw: null,
      asAccept: null,
      asString: null,
    },
    availableFileInputs: 0,
    selectedPages: '',
    selectedPagesError: '',
    selectedPagesValidation: /^[ ,\-\d]+$/,
    selectedPagesValidationError: 'SDE_USE_ONLY_NUMBERS',
  };

  output: any = {
    outputRequired: true,
    currentCloudRepo: {},
    fileName: null,
    encryptionPassword: null,
    passwordProtection: false,
    sendEncryptedEmail: false,
    sendEncryptedEmailLabel: '',
    file: {
      appendDateTimeToFilename: true,
      description: '',
      extension: 0,
      name: '',
    },
    addDateTime: true,
    datetimestr: null,
    fileNameError: '',
    fileNameValidation: /^[^"?;<>|:'*%^&#+`\\\/]+$/,
    fileTypes: {
      raw: null,
      asArray: [],
      extension: { key: '', value: '' },
    },
    outputType: 0, // a mask condition of displaying any output option.
    selector: {
      items: [
        {
          label: 'SDE_EMAIL2',
          value: 'Email',
          glyph: 'xgl-envelope',
          isDefault: true,
          isOn: true,
        },
        {
          label: 'SDE_CLOUD_REPO',
          value: 'Cloud',
          glyph: 'xgl-cloud_connect',
          isDefault: false,
          isOn: false,
        },
      ],
    },
    email: '',
    confirmEmail: '',
    emailSendOnErrorValue: false,
    emailOptions: [
      { key: 'attachment', visible: 'false', value: 'false' },
      { key: 'sendCondition', visible: 'true', value: 'always' },
      { key: 'sourceLink', visible: 'true', value: 'true' },
      { key: 'encryptAndTrack', visible: 'true', value: 'true' },
      { key: 'label', visible: 'true', value: 'SDE_EMAIL2' },
      { key: 'glyph', visible: 'true', value: 'xgl-envelope' },
    ],
  };
  outputSelected = true;
  outputOption = this.output.selector.items.find((x: any) => x.isDefault);
  OutputType = OutputType;

  // conditions
  occurrenceOptions: any = [
    {
      label: 'SDE_HOURLY',
      value: Occurrence.Hourly,
    },
    {
      label: 'SDE_DAILY',
      value: Occurrence.Daily,
    },
    {
      label: 'SDE_WEEKLY',
      value: Occurrence.Weekly,
    },
  ];
  occurrenceOption: any = this.occurrenceOptions[0];
  Occurrence = Occurrence;

  occurrenceDaysOfTheWeek: any = [
    { key: 'SDE_SUNDAY', value: 0 },
    { key: 'SDE_MONDAY', value: 1 },
    { key: 'SDE_TUESDAY', value: 2 },
    { key: 'SDE_WEDNESDAY', value: 3 },
    { key: 'SDE_THURSDAY', value: 4 },
    { key: 'SDE_FRIDAY', value: 5 },
    { key: 'SDE_SATURDAY', value: 6 },
  ];
  occurrenceTime: Time;
  selectedDayOfTheWeek = this.occurrenceDaysOfTheWeek[0];

  matchOptions = [
    {
      label: 'SDE_MATCHES1',
      value: EmailFieldMatchMode.Matches,
      isChecked: false,
    },
    {
      label: 'SDE_CONTAINS',
      value: EmailFieldMatchMode.Contains,
      isChecked: false,
    },
  ];
  emailMatchOption: any = this.matchOptions[0];
  subjectMatchOption: any = this.matchOptions[0];

  defaultEmailOptions: EmailOptions = {
    emailAddress: '',
    from: { queryStr: '', matchMode: this.emailMatchOption.value },
    subject: { queryStr: '', matchMode: this.subjectMatchOption.value },
    processAttachments: true,
    processMessage: false,
  };
  emailOptions = this.defaultEmailOptions;
  addressPlaceholder = 'SDE_ENTER_ADDRESS2';
  subjectPlaceholder = 'SDE_ENTER_A_SUBJECT';
  containingPlaceholder = 'SDE_ENTER_CONTAINING_STRING';

  // service
  selectedWorkflow: AutomatedWorkflow | null;
  serviceIsSelected = true;
  isValidatingService = false;

  // name and icon
  workflowName: string;
  selectedIcon: Icon | undefined;
  originalIcon: Icon;
  iconOptions = [
    { value: true, label: 'SDE_DEFAULT', isChecked: false },
    { value: false, label: 'SDE_CUSTOM', isChecked: false },
  ];
  iconOption = this.iconOptions[0];
  iconSize: IconSize = IconSize.Small;
  isEnabled = true;

  private xdaTranslatePipe: XdaTranslate;

  constructor(
    public activeModal: NgbActiveModal,
    private workflowService: WorkflowService,
    private automatedWorkflowService: AutomatedWorkflowService,
    private modalService: NgbModal,
    private emailService: EmailService,
    private workflowHelper: WorkflowHelperService,
    private xdaStringsService: XdaStringsService,
    private userService: UserService,
    public xaccService: XaccService,
    private accessListService: AccessListService,
  ) {
    this.xdaTranslatePipe = new XdaTranslate(this.xdaStringsService);
    this.addressPlaceholder =
      this.xdaStringsService.strings[this.addressPlaceholder];
    this.subjectPlaceholder =
      this.xdaStringsService.strings[this.subjectPlaceholder];
    this.containingPlaceholder =
      this.xdaStringsService.strings[this.containingPlaceholder];
    this.googlePolicyStr = `${this.xdaTranslatePipe.transform('SDE_FMTSTR_USE_TRANSFER', ["Workflow Central's"])} <a href="https://developers.google.com/terms/api-services-user-data-policy#additional_requirements_for_specific_api_scopes" target="_blank">${this.xdaTranslatePipe.transform('SDE_FMTSTR_API_SERVICES', ['Google'])}</a>${this.xdaTranslatePipe.transform('SDE_INCLUDING_LIMITED_USE')}`;
    this.allowedEmailDomains =
      this.userService.isAdvancedPlan &&
      this.accessListService.config?.enableDomainRestrictions
        ? this.xdaTranslatePipe.transform('SDE_FOLLOWING_EMAIL_DOMAINS', [
            this.accessListService.config.allowedDomains.join(', '),
          ])
        : '';

    // cloud repo directory selected or access token refreshed and directory validated
    this.cloudRepoSub = this.xaccService.directoryOrFilesSelected.subscribe(
      (repoOptions) => {
        this.updateCloudRepoOptions(repoOptions);

        // revalidate the input or output step after selecting a directory or file.
        const step = this.selectInputRepo
          ? this.getStep('input')
          : this.getStep('output');

        if (step) {
          step.status = this.validateStep(step)
            ? StepStatus.Valid
            : StepStatus.Invalid;
        }
      },
    );

    // Outlook sign-in or refreshing connector access token
    this.xaccServiceLoginSub = this.xaccService.xaccLoginSucceed.subscribe(
      (data) => {
        if (data.repo.connector === 'GraphOutlook') {
          // if input email changed, invalidate output repo and folder
          if (
            this.defaultEmailOptions.emailAddress &&
            this.defaultEmailOptions.emailAddress !== data.profile.mail
          ) {
            this.output.currentCloudRepo =
              this.selectedOutputCloudRepo =
              this.selectedOutputCloudFolder =
                null;
          }
          this.emailOptions.emailAddress =
            this.defaultEmailOptions.emailAddress = data.profile.mail;
        }
        this.input.currentCloudRepo = data.repo;

        if (
          this.input.currentCloudRepo.connector.slice(0, 5) ===
          this.output.currentCloudRepo?.connector?.slice(0, 5)
        ) {
          // always update the output access token in case the input token was refreshed.
          this.output.currentCloudRepo.access = data.repo.access;
        }

        // email login successful, clear any previous input repo selection
        this.selectedInputCloudRepo = this.selectedInputCloudFolder = null;

        // revalidate the input step after successful login
        const step = this.getStep('input');
        if (step) {
          step.status = this.validateStep(step)
            ? StepStatus.Valid
            : StepStatus.Invalid;
        }
      },
    );
  }

  async ngOnInit() {
    if (this.isNew) {
      this.resetInputSettings();
      this.resetConditionsSettings();
      this.resetServiceSettings();
      this.resetOutputSettings();
      this.resetNameSettings();
    } else {
      // validate all the steps and set first one to edit
      for (const step of this.steps) {
        step.status = this.validateStep(step)
          ? StepStatus.Valid
          : StepStatus.Invalid;
        step.mode = step.index === 0 ? StepMode.Edit : StepMode.Inactive;
      }

      if (this._workflow.version === "4.02") {
        this.workflowWarning = true;
        this.setStepStatus("workflow", StepStatus.Warning);
      }

      if (this._workflow.input.cloudRepoOptions) {
        try {
          // validate input cloud repo access token, if output cloud repo it will also get updated with new token on reentry from xacc.
          await this.xaccService.validateRepoToken(
            this._workflow.input.cloudRepoOptions as MWCloudRepo,
          );

          // validate the input directory
          if (this._workflow.input.cloudRepoOptions.dir) {
            await this.xaccService.validatePath(
              this._workflow.input.cloudRepoOptions as MWCloudRepo,
            );
          }
        } catch (e: any) {
          if (e?.message == 'BROWSE_FOLDER_REQUIRED') {
            this._workflow.input.cloudRepoOptions.sitePath =
              this._workflow.input.cloudRepoOptions.dir = null;
            this.selectedInputCloudFolder = null;
            this.inputCloudRepoIsInvalid = true;
          } else {
            this.selectInputRepo = true;
            this.xaccService.returningWebLogin(
              this._workflow.input.cloudRepoOptions,
            );
          }
        }
      }
    }
  }

  ngAfterViewInit() {
    this.serviceParams = this.serviceParamsQL.toArray();
    this.serviceParamsQL.changes.subscribe(async () => {
      this.serviceParams = this.serviceParamsQL.toArray();
      if (this.serviceParams.length && !this.isNew) {
        // saved autoworkflow, the workflow settings component just loaded so do a validation if not on the workflow step
        const step = this.getStep('workflow');
        if (step && step != this.step) {
          step.status = this.validateStep(step)
            ? StepStatus.Valid
            : StepStatus.Invalid;
        }
      }
    });
  }

  selectWorkflow(value: Workflow) {
    // ignore selection if it hasn't changed
    if (this._workflow.parentId === value.id) {
      return;
    }

    let services: Service[] = [];
    // deal with cached service objects
    if (typeof value.services[0] === 'string') {
      value.services.forEach((x) =>
        services.push(
          this.workflowService.getSqdService(x as string, value.type === 1),
        ),
      );
      value.services = services;
    }
    else {
      services = value.services as Service[];
    }

    this.serviceIsSelected = true;

    // Make a deep copy of workflow. Preserve workflow id, owner and creator
    const id = !this.isNew && this._workflow?.id ? this._workflow.id : '';
    const owner = this._workflow.owner;
    const creator = this._workflow.creator;
    const autoWorklowOnEditing = JSON.parse(JSON.stringify(this._workflow));
    this._workflow = JSON.parse(JSON.stringify(value));

    // transfer autoWorkflow setting on editing
    if (!this.isNew) {
      this._workflow.nextRun = autoWorklowOnEditing.nextRun;
      this._workflow.lastRun = autoWorklowOnEditing.lastRun;
      this._workflow.timezoneOffset = autoWorklowOnEditing.timezoneOffset;
      this._workflow.emailOptions = autoWorklowOnEditing.emailOptions;
      this._workflow.output = autoWorklowOnEditing.output;
    }

    if (id) {
      this._workflow.id = id;
    }
    this._workflow.owner = owner;
    this._workflow.creator = creator;
    this._workflow.parentId = value.id;

    this.onFirstService = true;

    if (this._workflow.services?.length == 1) {
      this.onLastService = true;
    }

    this.originalServices = JSON.parse(JSON.stringify(this._workflow.services));
    this.availableFileOutputs =
      services[services.length - 1].availableFileOutputs;
    this.lastService = services[services.length - 1];
    this.isTranslateWorkflow = this.lastService.name
      .toLowerCase()
      .includes('translat');

    this.setupOutputOnLoad(this.availableFileOutputs);
    this.setServiceSettings();

    // changing the workflow automatically changes output file types so output settings are reset to default, warn user if it's been changed
    const outputStep = this.getStep('output');
    if (outputStep && outputStep.status !== StepStatus.Default) {
      outputStep.status = StepStatus.Warning;
    }

    // store the original service setting for reset service setting.
    this.originalServiceSettings = JSON.parse(
      JSON.stringify(this.serviceSettings),
    );

    // if original icon is not set or it's not a custom glyph use the selected workflow icon
    if (!this.originalIcon || this.originalIcon.image) {
      this.originalIcon = JSON.parse(JSON.stringify(this._workflow.icon));
    }

    // if icon has already been selected then update it if it's the default otherwise set workflow to the custom icon.
    if (this.iconOption.label == 'SDE_DEFAULT') {
      // warn user the default icon changed
      const nameStep = this.getStep('name');
      if (nameStep && nameStep.status !== StepStatus.Default) {
        nameStep.status = StepStatus.Warning;
      }
    } else {
      this._workflow.icon = this.selectedIcon || this.originalIcon;
    }

    this.resetStepStatus('workflow');
  }

  statusGlyph(step: Step) {
    switch (step.status) {
      // don't show checkmark or warning glyph in edit mode
      case StepStatus.Valid:
        return step.mode !== StepMode.Edit
          ? 'xgl-checkmark_circle_alt valid'
          : '';
      case StepStatus.Warning:
        return step.mode !== StepMode.Edit ? 'xgl-alert_alt warning' : '';
      case StepStatus.Invalid:
        return 'xgl-fault_alt error';
      default:
        return '';
    }
  }

  setStepStatus(stepName: string, status: StepStatus) {
    const step = this.getStep(stepName);
    if (step) {
      step.status = status;
    }
  }

  resetStepStatus(stepName: string) {
    const step = this.getStep(stepName);

    // if status is other than the default, it needs to be revalidated
    if (step && step.status != StepStatus.Default) {
      step.status = StepStatus.Revalidate;
    }
  }

  stepTo(stepTo: Step) {
    if (stepTo.index > this.step.index) {
      stepTo = stepTo.hidden ? this.steps[stepTo.index + 1] : stepTo;
      this.stepForward(this.step, stepTo);
    } else if (stepTo.index < this.step.index) {
      stepTo = stepTo.hidden ? this.steps[stepTo.index - 1] : stepTo;
      this.stepBack(this.step, stepTo);
    }

    // if the stepTo is in error, do validation to show invalid fields
    if (this.steps[stepTo.index].status === StepStatus.Invalid) {
      setTimeout(() => {
        this.validateStep(stepTo);
      });
    }
  }

  stepForward(step: Step, stepTo: Step | null = null) {
    this.validateStep(step);

    if (this.isGoodToGo) {
      //advance the step
      this.step.status = StepStatus.Valid;
      this.step.mode = StepMode.Inactive;
      this.step = stepTo ? stepTo : this.steps[step.index + 1];
      this.step.mode = StepMode.Edit;

      // re-validate any dependent steps with required fields or dependent settings
      if (step.name === 'input') {
        const steps = ['conditions', 'output'];
        for (const stepName of steps) {
          const step = this.getStep(stepName);
          if (step && step.status !== StepStatus.Default) {
            step.status = this.validateStep(step)
              ? StepStatus.Valid
              : StepStatus.Invalid;
          }
        }
      }

      if (this.step.name === 'name' && !this.selectedIcon) {
        this.selectedIcon = this._workflow.icon;
      }
    } else if (
      this.step.status === StepStatus.Valid ||
      this.step.status === StepStatus.Revalidate
    ) {
      // don't advance, if step was previously valid, mark it invalid
      this.step.status = StepStatus.Invalid;
    }

    return;
  }

  stepBack(step: Step, stepTo: Step | null = null) {
    if (step.index > 0) {
      if (this.step.name === 'workflow' && this.servicePageIndex > 0) {
        this.servicePageIndex--;
        return;
      }

      // if step was previously validated, then validate on back.
      if (step.status !== StepStatus.Default) {
        step.status = this.validateStep(step)
          ? StepStatus.Valid
          : StepStatus.Invalid;
        if (step.status !== StepStatus.Valid) {
          return;
        }
      }

      // step back
      this.step.mode = StepMode.Inactive;
      this.step = stepTo ? stepTo : this.steps[step.index - 1];
      this.step.mode = StepMode.Edit;
    }
  }

  validateStep(step: Step) {
    this.isGoodToGo = true;

    if (step.hidden) {
      return this.isGoodToGo;
    }

    if (step.index < this.steps.length) {
      if (step.name === 'input') {
        this.errorInput = '';
        this.inputCloudRepoIsInvalid = false;

        if (this.inputSource.value === InputSource.Cloud) {
          if (!this.selectedInputCloudRepo || !this.selectedInputCloudFolder) {
            this.inputCloudRepoIsInvalid = true;
            this.isGoodToGo = false;
          } else if (!this.input.currentCloudRepo.access) {
            // no access token
            this.isGoodToGo = false;
          }
          // if output connector is not the same then clear the output folder
          else if (
            this.output.currentCloudRepo?.connector &&
            this.output.currentCloudRepo.connector.slice(0, 5) !=
              this.input.currentCloudRepo.connector?.slice(0, 5)
          ) {
            this.selectedOutputCloudFolder = null;

            // set output repo to input repo
            this.selectedOutputCloudRepo = this.selectedInputCloudRepo;
            this.output.currentCloudRepo = this.copyCloudRepoConnection(
              this.input.currentCloudRepo,
            );
          }
        } else if (this.inputSource.value === InputSource.Email) {
          if (
            !this.input.currentCloudRepo?.access &&
            this.emailOptions.emailAddress
          ) {
            // no access token, reset email address to require sign-in
            this.emailOptions.emailAddress = '';
          }

          if (
            !this.emailOptions.emailAddress ||
            this.emailOptions.emailAddress.length < 1
          ) {
            //TODO add validation or check validity of email field?
            this.errorInput = '';
            setTimeout(() => {
              this.errorInput = 'SDE_SIGN_CONTINUE';
            });
            this.isGoodToGo = false;
          }

          // if output repo is not Microsoft, then clear selected output repo and folder.
          if (!this.output.currentCloudRepo?.connector?.startsWith('Graph')) {
            this.output.currentCloudRepo =
              this.selectedOutputCloudRepo =
              this.selectedOutputCloudFolder =
                null;
          }

          // clear input repo and folder
          this.input.currentCloudRepo.sitePath =
            this.input.currentCloudRepo.dir =
            this.input.currentCloudRepo.files =
              null;
          this.selectedInputCloudRepo = this.selectedInputCloudFolder = null;
        }

        // limit output cloud repos to Microsoft Graph API if input is email or Graph API
        this.outputCloudRepos = [];
        if (
          this.inputSource.value === InputSource.Email ||
          this.input.currentCloudRepo?.connector.startsWith('Graph')
        ) {
          this.cloudRepos
            .filter((x) => x.value.startsWith('Graph'))
            .forEach((repo) => {
              this.outputCloudRepos.push(repo);
            });
          // if there's only one, make it selected
          if (this.outputCloudRepos.length === 1) {
            this.selectedOutputCloudRepo = this.outputCloudRepos[0];
            this.output.currentCloudRepo = this.copyCloudRepoConnection(
              this.input.currentCloudRepo,
            );
          }
        }
        // otherwise make output repo the same as the selected input repo
        else if (this.selectedInputCloudRepo && this.input.currentCloudRepo) {
          // if output repo is different then clear the selected output folder
          if (
            this.input.currentCloudRepo.connector !=
            this.output.currentCloudRepo?.connector
          ) {
            this.output.currentCloudRepo = this.copyCloudRepoConnection(
              this.input.currentCloudRepo,
            );
            this.selectedOutputCloudFolder = null;
          }
          this.outputCloudRepos.push(this.selectedInputCloudRepo);
          this.selectedOutputCloudRepo = this.selectedInputCloudRepo;
        }
        // Set the output cloud repo radio label, if there's only one then use the name of the repo
        this.output.selector.items.find((x: any) => x.value === 'Cloud').label =
          this.outputCloudRepos.length === 1
            ? this.outputCloudRepos[0].key
            : 'SDE_CLOUD_REPO';

        // validate page range
        if (this.isGoodToGo && !this.validateRange()) {
          this.isGoodToGo = false;
        }
      } else if (
        step.name === 'conditions' &&
        this.inputSource.value === InputSource.Email
      ) {
        this.errorConditions = '';
        this.emailOptions.from.queryStr =
          this.emailOptions.from.queryStr.trim();
        this.emailOptions.subject.queryStr =
          this.emailOptions.subject.queryStr.trim();
        if (
          !this.emailOptions.from.queryStr.length &&
          !this.emailOptions.subject.queryStr.length
        ) {
          this.errorConditions = '';
          setTimeout(() => {
            this.errorConditions = 'SDE_FROM_EMAIL_ADDRESS7';
          });
          this.setError('emailMatch');
          this.setError('subjectMatch');
          this.isGoodToGo = false;
        } else if (
          this.emailOptions.from.queryStr &&
          this.emailMatchOption.value === EmailFieldMatchMode.Matches &&
          !this.emailService.isValidEmailAddress(
            this.emailOptions.from.queryStr,
          )
        ) {
          this.errorConditions = '';
          setTimeout(() => {
            this.errorConditions = 'SDE_EMAIL_NOT_VALID';
          });
          this.setError('emailMatch');
          this.isGoodToGo = false;
        }
        if (
          !this.emailOptions.processAttachments &&
          !this.emailOptions.processMessage
        ) {
          this.isGoodToGo = false;
        }
      } else if (step.name === 'workflow') {
        this.serviceIsSelected = this.selectedWorkflow ? true : false;
        if (!this.serviceIsSelected) {
          this.isGoodToGo = false;
        } else if (!this.serviceParams.length) {
          // The workflow service setting component isn't loaded yet, do validation for the Select or PDF Convert setting (for error such as invalid language).
          // This occurs when the autoworkflow is loaded and all steps are validated.
          this.serviceSettings[this.servicePageIndex].settings.forEach(
            (setting) => {
              if (
                setting.value &&
                setting.type === SettingType.PdfConvert &&
                JSON.parse(setting.value).format !== 'searchable'
              ) {
                return;
              }
              const selectedValue =
                setting.type !== SettingType.PdfConvert
                  ? setting.selectedValue
                  : setting.selectedValue2;
              if (!selectedValue && setting.required && setting.error?.length) {
                this.isGoodToGo = false;
                return;
              }
            },
          );
        } else {
          // The workflow service setting component is loaded, call the validation which will trigger validateServiceSettings().
          // This occurs when stepping to and from the workflow step, the workflow settings component has been loaded for the UI.
          this.isValidatingService = true;
          this.serviceParams.forEach((setting) => {
            if (setting.control) {
              setting.control.validate();
            }
          });
          this.isValidatingService = false;
        }

        if (this.isGoodToGo) {
          if (this.servicePageIndex < this.serviceSettings.length - 1) {
            this.servicePageIndex++;
            return this.isGoodToGo;
          }

          this.ignorePageRange = false;
          const serviceSettings = this.serviceSettings[this.servicePageIndex];

          // For Protect workflow, check emailDocument setting to indicate if output is required and to force the output destination setting
          if (
            serviceSettings.name === 'SDE_DOCUMENT_PROTECTION' &&
            serviceSettings.settings[0].value
          ) {
            const settings = JSON.parse(serviceSettings.settings[0].value);
            this.workflow.output.outputRequired = !settings.emailDocument; // output is required if RPost is not emailing the document

            // force the output selection based on Protect email setting (if true then output is email otherwise cloud repo)
            this.selectOutput(
              settings.emailDocument
                ? this.output.selector.items[0]
                : this.output.selector.items[1],
            );

            // if Protect is emailing the document, set output.email for the output summary card to show the recipient emails.
            if (settings.emailDocument) {
              this.output.email = this.output.confirmEmail =
                settings.documentOptions.recipients
                  .map((recipient: any) => recipient.email)
                  .join(', ');
            }

            // if protect email is enabled then disable other output options
            this.output.selector.items.forEach((item: any) => {
              item.disabled =
                item.value == 'Email'
                  ? !settings.emailDocument
                  : settings.emailDocument;
              item.isOn = !item.disabled;
            });

            // if serviceParams aren't loaded then do the xrx-drm component validation for recipient email domains and domains list
            if (
              !this.serviceParams.length &&
              this.userService.isAdvancedPlan &&
              this.accessListService.config?.enableDomainRestrictions
            ) {
              if (settings.documentOptions.recipients?.length) {
                this.isGoodToGo = !settings.documentOptions.recipients.some(
                  (recipient: any) =>
                    this.accessListService.isAllowedDomain(recipient.email) ==
                    false,
                );
              }

              if (
                this.isGoodToGo &&
                settings.securityMode.label === 'SDE_TRACK_VIEWERS' &&
                settings.documentOptions.domains?.length
              ) {
                const domains = settings.documentOptions.domains.split(',');
                this.isGoodToGo = !domains.some(
                  (domain: any) =>
                    this.accessListService.isAllowedDomain(domain) == false,
                );
              }
            }
          } else {
            this.output.selector.items.forEach((item: any) => {
              item.disabled = false;
            }); // enable all
          }
        }
      } else if (step.name === 'output') {
        this.outputCloudRepoIsInvalid = false;

        if (
          this.output.selector.items.find((x: Item) => x.value === 'Cloud').isOn
        ) {
          if (
            this.selectedOutputCloudFolder &&
            this.inputSource.value === InputSource.Cloud &&
            this.output.currentCloudRepo.connector.slice(0, 5) !==
              this.input.currentCloudRepo.connector?.slice(0, 5)
          ) {
            // folder selected but input and output cloud repos are not the same  (compare first 5 chars so different Graph API connectors match)
            this.selectedOutputCloudFolder = null; // clear the selected folder
            this.outputCloudRepoIsInvalid = true;
            this.isGoodToGo = false;
          } else if (!this.selectedOutputCloudFolder) {
            // no selected folder
            this.outputCloudRepoIsInvalid = true;
            this.isGoodToGo = false;
          }
        } else {
          // email selected
          if (
            this.serviceSettings[this.servicePageIndex].name !==
            'SDE_DOCUMENT_PROTECTION'
          ) {
            // ignore for Protect since RPost sends the email
            if (
              !this.output.email ||
              !this.output.confirmEmail ||
              this.output.email.toLowerCase() !=
                this.output.confirmEmail.toLowerCase()
            ) {
              const recipientEmail = this.form.form.get('recipientEmail');
              const confirmEmail = this.form.form.get('confirmEmail');
              if (recipientEmail && confirmEmail) {
                recipientEmail.markAsTouched();
                recipientEmail.markAsDirty();
                confirmEmail.markAsTouched();
                confirmEmail.markAsDirty();
                this.isGoodToGo = false;
              }
            }

            // validate the email domain
            if (
              this.output.email &&
              !this.accessListService.isAllowedDomain(this.output.email)
            ) {
              this.errorEmail = '';
              setTimeout(() => {
                this.errorEmail = 'SDE_EMAIL_DOMAIN_NOT2';
              }, 100);
              this.setError('confirmEmail');
              this.isGoodToGo = false;
            }
          }

          // clear output repo folder
          this.selectedOutputCloudFolder = null;
        }
      } else if (step.name === 'name') {
        this.errorName = '';

        // check the workflow name is not all whitespace
        this.workflowName = this.workflowName.trim();
        if (!this.workflowName.replace(/\s/g, '').length) {
          this.form.form.get('name')?.markAsTouched();
          this.form.form.get('name')?.markAsDirty();
          this.isGoodToGo = false;
        }

        // check for trailing "." to prevent error creating Processed folder w/OneDrive or SharePoint
        if (this.workflowName.endsWith('.')) {
          this.errorName = '';
          setTimeout(() => {
            this.errorName = this.xdaStringsService.strings[
              'SDE_NAME_CANNOT_END'
            ].replace('{0}', '.');
          });
          this.setError('name');
          this.isGoodToGo = false;
        }

        // check if workflow name already exists
        if (
          this._workflow.id &&
          this.automatedWorkflowService.isWorkflowWithSameNameExist(
            this._workflow.id,
            this.workflowName,
          )
        ) {
          this.errorName = '';
          setTimeout(() => {
            this.errorName = 'SDE_WORKFLOW_BY_NAME';
          });
          this.setError('name');
          this.isGoodToGo = false;
        }

        // only image OR glyph (not both) should be set for the icon
        if (this._workflow.icon?.image && this._workflow.icon.glyph) {
          if (this.iconOption.label === 'SDE_DEFAULT') {
            this._workflow.icon.glyph = this._workflow.icon.color = '';
          } else {
            this._workflow.icon.image = '';
          }
        }
      }
    }
    return this.isGoodToGo;
  }

  matchConditionValidation(isValid: boolean) {
    if (isValid && this.errorConditions) {
      this.errorConditions = '';
      this.clearError('emailMatch');
      this.clearError('subjectMatch');
      if (this.step.status == StepStatus.Invalid) {
        this.step.status = StepStatus.Revalidate;
      }
    }
  }

  validateServiceSettings(e: any, field: any) {
    if (!this.serviceSettings[this.servicePageIndex].validations) {
      this.serviceSettings[this.servicePageIndex].validations = {};
    }

    this.serviceSettings[this.servicePageIndex].validations[field.name] = e;
    const values = Object.keys(
      this.serviceSettings[this.servicePageIndex].validations,
    ).map(
      (key) => this.serviceSettings[this.servicePageIndex].validations[key],
    );
    this.isGoodToGo = !values.includes(false);

    // only set invalid status if validating the workflow service settings
    if (this.isValidatingService || this.isGoodToGo) {
      const workflowStep = this.getStep('workflow');
      if (workflowStep) {
        workflowStep.status = this.isGoodToGo
          ? StepStatus.Valid
          : StepStatus.Invalid;
      }
    }
  }

  resetInputSettings() {
    this.setInput();

    this.resetStepStatus('input');
    this.isGoodToGo = true;
  }

  resetConditionsSettings() {
    this.setConditions();

    if (this.errorConditions) {
      this.errorConditions = '';
      this.clearError('emailMatch');
      this.clearError('subjectMatch');
    }

    this.resetStepStatus('conditions');
    this.isGoodToGo = true;
  }

  setConditions() {
    this.emailOptions = this._workflow.emailOptions
      ? JSON.parse(JSON.stringify(this._workflow.emailOptions))
      : JSON.parse(JSON.stringify(this.defaultEmailOptions));
    this.emailMatchOption = this.matchOptions.find(
      (x) => x.value == this.emailOptions.from.matchMode,
    );
    this.subjectMatchOption = this.matchOptions.find(
      (x) => x.value == this.emailOptions.subject.matchMode,
    );

    switch (this._workflow?.schedule?.occurrence) {
      default:
      case 'SDE_HOURLY':
        this.occurrenceOption = this.occurrenceOptions[0];
        break;
      case 'SDE_DAILY':
        this.occurrenceOption = this.occurrenceOptions[1];
        break;
      case 'SDE_WEEKLY':
        this.occurrenceOption = this.occurrenceOptions[2];
        break;
    }

    let scheduleDate;
    if (this._workflow?.schedule?.date) {
      scheduleDate = new Date(this._workflow.schedule.date);
      scheduleDate.setMinutes(Math.round(scheduleDate.getMinutes() / 15) * 15);
    } else {
      scheduleDate = new Date();
      if (this.occurrenceOption.value === Occurrence.Hourly) {
        scheduleDate.setHours(scheduleDate.getHours() + 1); // advance to the next hour
        scheduleDate.setMinutes(0);
      }
    }

    const hour = scheduleDate.getHours() % 12; // 12hr format
    const minute =
      scheduleDate.getMinutes() > 0
        ? scheduleDate.getMinutes().toString()
        : '00';
    this.occurrenceTime = {
      hour: hour === 0 ? '12' : hour.toString(),
      minute: minute,
      ampm: scheduleDate.getHours() < 12 ? 'am' : 'pm',
    };
    this.selectedDayOfTheWeek =
      this.occurrenceDaysOfTheWeek[scheduleDate.getDay()];
  }

  resetServiceSettings() {
    // restore the original service settings for the current service page only!
    if (this.originalServiceSettings) {
      this.serviceSettings[this.servicePageIndex] = JSON.parse(
        JSON.stringify(this.originalServiceSettings[this.servicePageIndex]),
      );
    }

    // set the xrx-setting component with the original setting values
    this.serviceParams.forEach((setting) => {
      const param = this.serviceSettings[this.servicePageIndex].settings.find(
        (x) => x.name === setting.name,
      );
      if (param) {
        // restore the values
        setting.value = param.value;
        setting.value2 = param.value2;
        // restore the selected values
        setting.setSelectedValue();
      }
    });

    this.resetStepStatus('workflow');
    this.isGoodToGo = true;
  }

  setOutputFormatIfIsTranslateService(fileName: string | null = null) {
    if (this.isTranslateWorkflow) {
      this.output.fileTypes = this.getTranslationOutputFormat(fileName);
    }
  }

  resetOutputSettings() {
    if (!this._workflow.output) {
      this._workflow.output = this.output;
    }

    // Ignore reset if output settings are not required (Protect workflow step set the output settings)
    if (!this.workflow.output.outputRequired) {
      return;
    }

    this.setupOutputOnLoad(this.availableFileOutputs);
    this.setupOutputCloudRepoOptions();
    this.setOutputSettings();
    this.resetStepStatus('output');
    this.isGoodToGo = true;
  }

  resetNameSettings() {
    this.workflowName = this.originalName;
    this._workflow.icon = this.originalIcon
      ? JSON.parse(JSON.stringify(this.originalIcon))
      : null;
    this.selectedIcon = JSON.parse(JSON.stringify(this._workflow.icon));

    this.iconOption = this.originalIcon?.glyph
      ? this.iconOptions[1]
      : this.iconOptions[0];
    this.isEnabled = this.originalEnabled;
    this.clearError('name');

    this.resetStepStatus('name');
    this.isGoodToGo = true;
  }

  setError(control: string) {
    const ctrl = this.form?.form?.get(control);
    if (ctrl) {
      setTimeout(() => {
        ctrl.setErrors({ customerror: true });
        ctrl.markAsTouched();
        ctrl.markAsDirty();
      }, 100);
    }
  }

  clearError(control: string) {
    const ctrl = this.form?.form?.get(control);
    if (ctrl) {
      ctrl.setErrors({});
      ctrl.markAsPristine();
      ctrl.updateValueAndValidity();
    }
  }

  // Convert fileType enum into usable string and get extension for output file
  setupOutputOnLoad(fType: FileType) {
    // Regex for do not allow the following characters: \/:*?"<>|';%^&#+`
    this.output.fileNameError = this.xdaTranslatePipe.transform(
      'SDE_CHARACTERS_CANNOT_BE',
      ['"?;<>|:\'*%^&#+`\\\/'],
    );
    this.output.fileTypes.raw = fType;
    this.output.fileTypes.asArray = this.workflowHelper
      .fileTypesToStrings(this.output.fileTypes.raw)
      .map((e) => {
        return { key: `.${e.toLowerCase()}`, value: e };
      });
    const value = this.workflow.output.file?.extension
      ? this.workflowHelper.fileTypesToStrings(
          this.workflow.output.file.extension,
        )[0]
      : this.workflowHelper.fileTypesToStrings(this.output.fileTypes.raw)[0];
    this.output.fileTypes.extension =
      this.output.fileTypes.asArray.find((e: any) => e.value === value) ||
      this.output.fileTypes.asArray[0];
    this.selectedTranslateFileType =
      this.isTranslateWorkflow &&
      this._workflow.output.file.extension === FileType.pdf
        ? this.translateFileTypes[1]
        : this.translateFileTypes[0];

    // If using a private/shared workflow use the saved values, or if changes have been made to the output options (email or print)
    // for Xerox workflows use the selector defaults
    if (this._workflow.type == 1 || this._workflow.output.outputOptions) {
      if (this._workflow.output.outputOptions?.length) {
        this.output.selector.items.forEach((item: Item) => {
          item.isOn = false;
        });
        this._workflow.output.outputOptions.forEach((option) => {
          const foundOutputOption = this.output.selector.items.find(
            (x: Item) => x.value === option.value,
          );
          if (foundOutputOption) {
            foundOutputOption.isOn = true;
          }
        });
      }
    } else {
      this.output.selector.items.forEach((item: Item) => {
        item.isOn = item.isDefault;
      });
    }

    this.outputOption = this.output.selector.items.find((x: Item) => x.isOn);

    this.output.confirmEmail = this.output.email = this._workflow.output
      .emailAddresses
      ? this._workflow.output.emailAddresses
      : this.userService.user?.email;
    this.output.sendEncryptedEmailLabel =
      this.xdaTranslatePipe.transform('SDE_ENCRYPT_TRACK_EMAIL1') +
      ' <a href="https://rmail.com" target="_blank">' +
      this.xdaTranslatePipe.transform('SDE_FMTSTR_BY_FMTSTR', [
        'RMail<sup>&reg;</sup>',
        'RPost<sup>&reg;</sup>',
      ]) +
      '</a>. ' +
      this.xdaTranslatePipe.transform('SDE_FMTSTR_CREDITS', ['5']);
    this.output.passwordProtection = this._workflow.output.passwordProtection;
    this.output.sendEncryptedEmail = this._workflow.output.sendEncryptedEmail;
    this.output.emailSendOnErrorValue = false;
    this.output.outputType = this._workflow.output.outputType;

    this.output.emailOptions.sourceLink =
      this.getOutputEmailOption('sourceLink');
    this.output.emailOptions.attachment =
      this.getOutputEmailOption('attachment');
    this.output.emailOptions.sendCondition =
      this.getOutputEmailOption('sendCondition');
    this.output.emailOptions.encryptAndTrack =
      this.getOutputEmailOption('encryptAndTrack');
    this.output.emailOptions.label = this.getOutputEmailOption('label');
    this.output.emailOptions.glyph = this.getOutputEmailOption('glyph');
    this.output.emailOptions.sendCondition =
      this.getOutputEmailOption('sendCondition');

    this.outputSelected = this.isOutputSelected();
  }

  getOutputEmailOption(optionKey: string) {
    const emOption = this.workflow.output.emailOptions?.find(
      (i) => i.key === optionKey,
    );
    return { value: emOption?.value, visible: emOption?.visible };
  }

  setOutputSettings() {
    let emailCondition = '',
      workflowOutputs = this.output.selector.items.filter((x: Item) => x.isOn);

    // clear and recreate the output settings based on selected options
    this._workflow.output.outputOptions = [];
    this._workflow.output.cloudRepoOptions = null;
    this._workflow.output.emailAddresses = '';
    this._workflow.output.fromEmailAddress = '';
    const emailOption = this._workflow.output.emailOptions?.find(
      (i) => i.key === 'sendCondition',
    );
    if (emailOption) {
      emailOption.value = EmailSendCondition.Never;
    }

    if (workflowOutputs.length > 0) {
      workflowOutputs.forEach((item: any) => {
        if (item.value == 'Email') {
          // For Protect workflow, email is handled by RPost
          if (
            this.serviceSettings[this.servicePageIndex]?.name ===
            'SDE_DOCUMENT_PROTECTION'
          ) {
            return;
          }
          this._workflow.output.emailAddresses = this.output.email;
          this._workflow.output.fromEmailAddress = this.output
            .sendEncryptedEmail
            ? this.userService.user?.email
            : '';

          // set email send condition
          if (
            this.output.selector.items.find((x: Item) => x.value === 'Email')
              .isOn
          ) {
            emailCondition = this.output.emailSendOnErrorValue
              ? EmailSendCondition.OnFailure
              : EmailSendCondition.Always;
          }
        }
        this._workflow.output.outputOptions?.push({
          key: 'outputType',
          value: item.value,
        });
      });
    } else {
      emailCondition = EmailSendCondition.Never;
    }

    this._workflow.output.file.appendDateTimeToFilename =
      this.output.addDateTime;
    if (emailOption) {
      emailOption.value = emailCondition;
    }

    this._workflow.output.cloudRepoOptions = this.output.selector.items.find(
      (x: Item) => x.value === 'Cloud',
    ).isOn
      ? this.output.currentCloudRepo
      : null;
    this._workflow.output.passwordProtection = this.output.passwordProtection;
    this._workflow.output.sendEncryptedEmail = this.output.sendEncryptedEmail;
    if (
      this.savingWorkflow &&
      !this._workflow.enabled &&
      this._workflow.output.cloudRepoOptions
    ) {
      this._workflow.output.cloudRepoOptions.access = null; // clear the access token on save if the workflow is disabled
    }
    if (
      !this._workflow.output.cloudRepoOptions &&
      this.outputCloudRepos.length > 1
    ) {
      this.selectedOutputCloudRepo = this.selectedOutputCloudFolder = null;
    }
  }

  setupOutputCloudRepoOptions() {
    this.outputCloudRepoIsInvalid = false;
    if (this._workflow.output.cloudRepoOptions?.connector) {
      const repoOptions = this._workflow.output.cloudRepoOptions;
      this.selectedOutputCloudRepo =
        this.cloudRepos.find((x) =>
          !repoOptions.region
            ? x.value === repoOptions.connector
            : x.value === repoOptions.connector &&
              x.region === repoOptions.region,
        ) || null;
      this.selectedOutputCloudFolder = repoOptions.dir ?? null;

      this.selectInputRepo = false;
      this.updateCloudRepoOptions(this._workflow.output.cloudRepoOptions);
    }
  }

  setInput() {
    this.input.selectedPages = this._workflow.input?.selectedPages ?? '';
    this.input.currentCloudRepo =
      this._workflow.input?.cloudRepoOptions ?? null;
    this.input.isCloudInput = this._workflow.input?.isCloudInput ?? true;
    this.emailOptions.emailAddress =
      this._workflow?.emailOptions?.emailAddress ?? '';
    this.selectedInputCloudRepo = this.selectedInputCloudFolder = null;
    this.inputCloudRepoIsInvalid = false;
    this.errorInput = '';
    this.pageRangeOption = this._workflow.input?.selectedPages
      ? this.pageRangeOptions[1]
      : this.pageRangeOptions[0];

    if (this.input.isCloudInput) {
      this.inputSource = this.inputSourceOptions.find(
        (x: any) => x.value === InputSource.Cloud,
      );
      const inputRepo = this._workflow.input?.cloudRepoOptions;
      if (inputRepo) {
        inputRepo.region = inputRepo.region == null ? '' : inputRepo.region;
        this.selectedInputCloudRepo =
          this.cloudRepos.find((x) =>
            !inputRepo?.region
              ? x.value === inputRepo.connector
              : x.value === inputRepo.connector &&
                x.region === inputRepo.region,
          ) || null;
        this.selectInputRepo = true;
        this.updateCloudRepoOptions(inputRepo);
      }
    } else {
      this.inputSource = this.inputSourceOptions.find(
        (x: any) => x.value === InputSource.Email,
      );
      this.selectedInputCloudRepo = this.selectedInputCloudFolder = null;
    }

    if (this.input.currentCloudRepo) {
      this.xaccService.setRepo({
        connector: this.input.currentCloudRepo.connector,
        region: this.input.currentCloudRepo.region,
        accessToken: this.input.currentCloudRepo.access,
        refreshToken: this.input.currentCloudRepo.refresh,
        sitePath: this.input.currentCloudRepo.sitePath,
        dir: this.input.currentCloudRepo.dir,
        selectedFolders: [],
      });
      this.xaccService.repoOptions = this.input.currentCloudRepo;
    }
  }

  setServiceSettings() {
    let combinedSettings: CombinedServiceSettings = {
      name: '',
      settings: [],
      validations: {},
    };
    this.serviceSettings = [];
    this.serviceSettings.push(combinedSettings);

    this._workflow.services?.forEach((s, index) => {
      const service = s as Service;
      if (service.serviceSettings && service.serviceSettings.length) {
        //This service setting has own page
        if (index > 0 && service.pageBreak) {
          combinedSettings = { name: '', settings: [], validations: {} };
          combinedSettings.name = service.name;
          this.serviceSettings.push(combinedSettings);
        }

        combinedSettings.name = service.name;

        service.serviceSettings.forEach((param) => {
          combinedSettings.settings.push(param);
        });
      }
    });

    // store the original service setting for reset service setting.
    this.originalServiceSettings = JSON.parse(
      JSON.stringify(this.serviceSettings),
    );
  }

  // force xrx-input to change the value display to include or not include "[Date & Time]"
  refreshOutputFilename() {
    if (this.output.fileName) {
      const fileName = this.output.fileName;
      this.output.fileName = this.output.fileName + 1;
      setTimeout(() => {
        this.output.fileName = fileName;
      }, 10);
    }
  }

  getTranslationOutputFormat(fileName: any) {
    if (this.isTranslateWorkflow) {
      let inputType = this.lastService.availableFileInputs;
      const services = this._workflow.services as Service[];
      if (services.length > 1) {
        inputType = (services[services.length - 2] as Service)
          .availableFileOutputs;
      }
      return this.workflowHelper.getTranslateFileTypes(fileName, inputType);
    }
    return;
  }

  getFileFormat(fileName: string) {
    const index = fileName.lastIndexOf('.');
    if (index === -1) {
      return null;
    }
    return fileName.substr(index + 1).toLowerCase();
  }

  // validate the configuration hasn't changed that would disable the workflow
  validateConfig(workflow: AutomatedWorkflow) {
    return this.accessListService
      .getAccessConfigConfig(this.userService.user?.accessListId || '', false)
      .then((resp) => {
        if (resp) {
          const storedConfig = resp;

          if (storedConfig.disableAutoWorkflows) {
            throw new Error('SDE_ADMINISTRATOR_DISABLED_AUTOMATED');
          }
          const preconfiguredWorkflows = this.workflowService.workflows
            .filter(
              (x) =>
                x.type == 0 &&
                x.isSingleService &&
                !storedConfig.hiddenWorkflows.includes(x.id || ''),
            )
            .sort((a, b) => {
              const aTranslated =
                this.xdaStringsService.strings[a.name] || a.name;
              const bTranslated =
                this.xdaStringsService.strings[b.name] || b.name;
              return aTranslated.localeCompare(bTranslated);
            });

          if (
            preconfiguredWorkflows?.length === 0 ||
            (storedConfig &&
              workflow.parentId &&
              storedConfig.hiddenWorkflows.includes(workflow.parentId)) ||
            (storedConfig &&
              workflow.input.cloudRepoOptions?.connector &&
              storedConfig.disableCloudConnectors.includes(
                workflow.input.cloudRepoOptions.connector +
                  workflow.input.cloudRepoOptions?.region,
              )) ||
            (storedConfig &&
              workflow.output.cloudRepoOptions?.connector &&
              storedConfig.disableCloudConnectors.includes(
                workflow.output.cloudRepoOptions.connector +
                  workflow.output.cloudRepoOptions?.region,
              )) ||
            (storedConfig &&
              storedConfig.disableCloudConnectors.length ===
                Constants.middlewareDestinations.length &&
              workflow.output.outputOptions?.some((o) => o.value === 'Cloud'))
          ) {
            throw new Error('SDE_ADMINISTRATOR_DISABLED_ONE');
          }
        }
      })
      .catch((error) => {
        throw error;
      });
  }

  saveWorkflow(f: NgForm) {
    this.savingWorkflow = true; // prevent duplicate saves

    // validate all the steps
    for (const step of this.steps) {
      this.validateStep(step);
      if (!this.isGoodToGo) {
        if (step.status !== StepStatus.Default) {
          step.status = StepStatus.Invalid;
        }
        this.savingWorkflow = false;
        return;
      } else {
        step.status = StepStatus.Valid;
      }
    }

    this.setWorkflowUserSelectedSettings();
    this.setOutputSettings();

    // Make a deep copy of workflow
    const originalWorkflow: AutomatedWorkflow = JSON.parse(
      JSON.stringify(this.workflow),
    );

    this._workflow.preferredLanguage = this.userService.user?.language || 'en';
    this._workflow.parentId = originalWorkflow.parentId
      ? originalWorkflow.parentId
      : originalWorkflow.id;

    // validate workflow against current configuration settings
    this.validateConfig(this.workflow).then(
      () => {
        if (this.isNew) {
          this.automatedWorkflowService.createWorkflow(this.workflow).subscribe(
            (newWorkflowId) => {
              if (newWorkflowId) {
                this._workflow.id = newWorkflowId;
                this.update.emit(null);
                this.activeModal.close();
                this.savingWorkflow = false;
              }
            },
            (error) => {
              const modalRef = this.modalService.open(BasicAlertComponent);
              modalRef.componentInstance.title = 'SDE_ERROR_CREATING_WORKFLOW';
              this.savingWorkflow = false;
            },
          );
        } else {
          this.automatedWorkflowService.saveWorkflow(this.workflow).subscribe(
            () => {
              this.update.emit(null);
              this.activeModal.close();
              this.savingWorkflow = false;
            },
            (error) => {
              const modalRef = this.modalService.open(BasicAlertComponent);
              modalRef.componentInstance.title = 'SDE_ERROR_CREATING_WORKFLOW';
              this.savingWorkflow = false;
            },
          );
        }
      },
      (error) => {
        const modalRef = this.modalService.open(BasicAlertComponent);
        modalRef.componentInstance.title = 'SDE_ERROR_CREATING_WORKFLOW';
        modalRef.componentInstance.body = error.message;
        this.savingWorkflow = false;
      },
    );
  }

  /**
   * call by saveWorkflow
   */
  setWorkflowUserSelectedSettings() {
    this._workflow.name = this.workflowName;
    this._workflow.enabled = this.isEnabled;
    this._workflow.input = JSON.parse(JSON.stringify(this.input)); // replace with user selected input settings.
    this._workflow.input.cloudRepoOptions = this.input.currentCloudRepo;
    if (this._workflow.input.cloudRepoOptions) {
      this._workflow.input.cloudRepoOptions.files = null;
    }
    this._workflow.input.availableFileInputs = (
      this._workflow.services as Service[]
    )[0].availableFileInputs;
    this._workflow.input.selectedPages =
      this.pageRangeOption.value == 'SDE_SELECT_PAGES' && !this.ignorePageRange
        ? this.input.selectedPages
        : '';
    this._workflow.input.isCloudInput =
      this.inputSource.value === InputSource.Cloud;
    if (
      this.savingWorkflow &&
      !this._workflow.enabled &&
      this._workflow.input.cloudRepoOptions
    ) {
      this._workflow.input.cloudRepoOptions.access = null; // clear the access token on save if the workflow is disabled
    }

    this.setWorkflowConditions();
    this.setUserSelectedServiceSettings();
    if (
      this.isTranslateWorkflow &&
      this.selectedTranslateFileType.value === 'Auto'
    ) {
      this._workflow.output.file.extension = 0; // undefined - preserves file extension for output file
    } else {
      this._workflow.output.file.extension = FileType[
        this.output.fileTypes.extension.value
      ] as any;
    }
    this._workflow.output.file.appendDateTimeToFilename =
      this.output.addDateTime;
  }

  setWorkflowConditions() {
    if (this.inputSource.value === InputSource.Email) {
      this._workflow.emailOptions = JSON.parse(
        JSON.stringify(this.emailOptions),
      );
    }

    // set schedule based on occurrence option
    const now = new Date();
    if (this.occurrenceOption.value != Occurrence.Hourly) {
      const occurrenceHour =
        (parseInt(this.occurrenceTime.hour) % 12) +
        (this.occurrenceTime.ampm === 'am' ? 0 : 12);
      now.setHours(occurrenceHour, parseInt(this.occurrenceTime.minute), 0, 0);
    }

    if (this.occurrenceOption.value === Occurrence.Weekly) {
      const selectedDay = this.selectedDayOfTheWeek.value;
      if (selectedDay > now.getDay()) {
        now.setDate(now.getDate() + selectedDay - now.getDay());
      } else if (selectedDay < now.getDay()) {
        now.setDate(now.getDate() + selectedDay - now.getDay() + 7);
      }
    }
    this._workflow.schedule = {
      occurrence: this.occurrenceOption.label,
      date: now.toISOString(),
      occurrenceUpdated: true,
    };
  }

  setUserSelectedServiceSettings() {
    const services = this._workflow.services as Service[];
    this.serviceSettings.forEach((s) => {
      const svsettings = services.find((x) => x.name === s.name);
      if (svsettings) {
        svsettings.serviceSettings = s.settings;
      }
    });
  }

  updateOutputOptions(outputOption: Item) {
    outputOption.isOn = !outputOption.isOn;
    this.outputSelected = this.isOutputSelected();
  }

  isOutputSelected() {
    return this.output.selector.items.some((item: Item) => item.isOn == true);
  }

  copyCloudRepoConnection(source: CloudRepo) {
    return {
      connector: source.connector,
      region: source.region || '',
      access: source.access,
      sitePath: null,
      dir: null,
      files: null,
    };
  }

  /**
   * Select output repo
   * @param cloudRepo
   * @param returning
   */
  selectCloudRepoOutput(
    cloudRepo: CloudDestination,
    returning = false,
  ) {
    if (
      !cloudRepo ||
      cloudRepo.value === this.output?.currentCloudRepo?.connector
    ) {
      // ignore selecting same connector
      return;
    }

    // clear current repo and folder and will get set to the selected repo and folder in updateCloudRepoOptions() when successfully login
    this.selectedOutputCloudRepo = this.selectedOutputCloudFolder = null;

    // if input and output are both Graph connectors, use same access token and browse to change folder instead of authenticating
    if (
      cloudRepo.value.startsWith('Graph') &&
      this.input.currentCloudRepo.connector.startsWith('Graph')
    ) {
      this.output.currentCloudRepo = this.copyCloudRepoConnection(
        this.input.currentCloudRepo,
      );
      this.output.currentCloudRepo.sitePath =
        this.output.currentCloudRepo.dir =
        this.output.currentCloudRepo.files =
          null;
      this.changeOutputCloudFolder(cloudRepo);
    } else {
      this.selectInputRepo = false;
      this.xaccService.initiateWebLogin(
        { connector: cloudRepo.value, region: cloudRepo.region },
        returning,
      );
      if (!returning) {
        this.updateCloudRepoOptions({
          connector: cloudRepo.value,
          region: cloudRepo.region,
        });
      }
    }
  }

  selectOutput(selected: Item) {
    this.output.selector.items.forEach((item: any) => {
      item.isOn = false;
    });
    selected.isOn = true;
    this.outputOption = selected;
  }

  changeInputCloudFolder(cloudRepo: CloudDestination) {
    this.selectInputRepo = true;
    this.input.currentCloudRepo.connector = cloudRepo.value;
    this.changeCloudFolder(this.input.currentCloudRepo);
  }

  changeOutputCloudFolder(cloudRepo: CloudDestination | null) {
    this.selectInputRepo = false;
    this.output.currentCloudRepo.connector = cloudRepo?.value;
    this.changeCloudFolder(this.output.currentCloudRepo);
  }

  changeCloudFolder(cloudRepo: CloudRepo) {
    this.xaccService.connector = cloudRepo.connector; // update xaccService.connector name due to changing between input and output connectors
    const modalRef = this.modalService.open(XaccBrowseModalComponent, {
      windowClass: 'xrx-modal',
      backdrop: 'static',
    });
    modalRef.componentInstance.returning =
      cloudRepo.sitePath || cloudRepo.dir ? true : false;
    modalRef.componentInstance.returningRepo = {
      connector: cloudRepo.connector,
      region: cloudRepo.region,
      accessToken: cloudRepo.access,
      refreshToken: cloudRepo.refresh,
      sitePath: cloudRepo.sitePath,
      dir: cloudRepo.dir,
      selectedFolders: null,
    };

    modalRef.result
      .then((repoOption) => {
        this.updateCloudRepoOptions(repoOption);
      })
      .catch((ex) => {
        // display repo login only if access is not set
        if (ex && cloudRepo.access === '') {
          this.selectCloudRepoOutput(
            { key: '', value: cloudRepo.connector, region: '' },
            true,
          );
        }
      });
  }

  updateCloudRepoOptions(repoOption: CloudRepo) {
    const selectedRepo = this.cloudRepos.find((x) =>
      !repoOption?.region
        ? x.value === repoOption.connector
        : x.value === repoOption.connector && x.region === repoOption.region,
    );

    if (this.selectInputRepo) {
      this.input.currentCloudRepo = repoOption;
      this.selectedInputCloudRepo = selectedRepo || null;
      this.input.cloudRepos = [];
      this.input.cloudRepos.push(repoOption);

      if (
        this.input.currentCloudRepo.connector.slice(0, 5) ===
        this.output.currentCloudRepo?.connector?.slice(0, 5)
      ) {
        // always update the output access token in case the input token was refreshed.
        this.output.currentCloudRepo.access = repoOption.access;
      }

      if (
        this._workflow?.input?.cloudRepoOptions &&
        this.input.currentCloudRepo.connector.slice(0, 5) ===
          this._workflow.input?.cloudRepoOptions?.connector.slice(0, 5)
      ) {
        // update the access token in existing workflow input settings
        this._workflow.input.cloudRepoOptions.access = repoOption.access;
      }

      // clear email setting since input repo is selected
      this.emailOptions.emailAddress = '';
    } else {
      this.output.currentCloudRepo = repoOption;
      this.selectedOutputCloudRepo = selectedRepo || null;
    }

    // If root folder selected use repo name
    if (repoOption?.dir?.name == '') {
      repoOption.dir.name = this.selectInputRepo
        ? this.selectedInputCloudRepo
          ? this.selectedInputCloudRepo?.key
          : this.selectedOutputCloudRepo
            ? this.selectedOutputCloudRepo?.key
            : ''
        : '';
    }

    if (this.selectInputRepo) {
      this.selectedInputCloudFolder = repoOption?.dir || null;
    } else {
      this.selectedOutputCloudFolder = repoOption?.dir || null;
    }
  }

  selectCloudRepo(cloudRepo: CloudDestination) {
    if (cloudRepo.value != this.input.currentCloudRepo?.connector) {
      // if changing between OneDrive and SharePoint update the connnector but retain the access token, and clear other repo settings
      if (
        cloudRepo.value.startsWith('Graph') &&
        this.input.currentCloudRepo?.connector?.startsWith('Graph')
      ) {
        this.input.currentCloudRepo.connector = cloudRepo.value;
        this.input.currentCloudRepo.sitePath =
          this.input.currentCloudRepo.dir =
          this.input.currentCloudRepo.files =
            null;
      } else {
        this.input.currentCloudRepo = null;
      }
      this.selectedInputCloudFolder = null;
      this.inputCloudRepoIsInvalid = false;
    }
  }

  signInRepo(cloudRepo: CloudDestination) {
    // sign in if repo changes or don't have an access token
    if (
      this.selectedInputCloudRepo?.value !==
        this.input?.currentCloudRepo?.connector ||
      !this.input?.currentCloudRepo?.access
    ) {
      this.selectInputRepo = true;

      // if have an access token then select a folder otherwise login
      if (this.input.currentCloudRepo) {
        if (this.input.currentCloudRepo.access) {
          this.changeInputCloudFolder(cloudRepo);
        } else if (this._workflow.input.cloudRepoOptions) {
          this.xaccService.returningWebLogin(
            this._workflow.input.cloudRepoOptions,
          );
        }
      } else {
        this.xaccService.initiateWebLogin(
          { connector: cloudRepo.value, region: cloudRepo.region },
          false,
        );
      }
    }
  }

  signInEmail() {
    this.xaccService.initiateWebLogin(
      { connector: 'GraphOutlook', region: '' },
      false,
    );
  }

  changeEmailAccount() {
    this.emailOptions.emailAddress = '';
  }

  selectEmailMatchOption(e: any, option: string) {
    if (option === 'email') {
      this.emailOptions.from.matchMode = e.value;
    } else if (option === 'subject') {
      this.emailOptions.subject.matchMode = e.value;
    }
  }

  selectIcon(iconOption: Item) {
    this._workflow.icon =
      iconOption.label === 'SDE_DEFAULT'
        ? this.selectedWorkflow?.icon
        : this.selectedIcon;
  }

  rangeOnKeyUp() {
    // set range input as touched if it is untouched
    const rangeInput = this.form.form.get('pageRange');
    if (rangeInput && !rangeInput?.touched) {
      rangeInput.markAsTouched();
    }

    this.isGoodToGo = true;
  }

  validateRange() {
    let pageRange = this.input.selectedPages,
      rangeInput = this.form?.form.get('pageRange'),
      isValid = true;

    // if rangeInput is null or pageRange is empty or all is selected then return isValid is true
    if (
      rangeInput == null ||
      pageRange == '' ||
      this.pageRangeOption.value == 'SDE_ALL'
    ) {
      isValid = true;
      return isValid;
    }

    // reset error
    this.input.selectedPagesError = '';

    // remove any trailing commas
    if (this.input.selectedPages.endsWith(',')) {
      // remove the last char
      pageRange = this.input.selectedPages = this.input.selectedPages.slice(
        0,
        -1,
      );
    }

    // make sure that each 'group' is comma separated
    if (/\d+\-\d+? +\d+|\d+ +\d+/g.test(pageRange)) {
      // set error, set isValid to false & return
      this.setRangeError(rangeInput, 'SDE_INVALID_PAGE_RANGE1');
      isValid = false;
      return isValid;
    }

    // remove white space before further processing processing
    pageRange = pageRange.replace(/\s/g, '');

    if (pageRange == '0') {
      // set error, set isValid to false & return
      this.setRangeError(rangeInput, 'SDE_INVALID_PAGE_RANGE1');
      isValid = false;
      return isValid;
    }

    const rangeSplit = pageRange.split(',');
    for (let i = 0; i < rangeSplit.length; i++) {
      // make sure each range is valid (i.e is a number or a range of numbers separated by a dash)
      if (!rangeSplit[i].match(/^\d+$/) && !rangeSplit[i].match(/^\d+\-\d+$/)) {
        // set error,set isValid to false & break out of loop
        this.setRangeError(rangeInput, 'SDE_INVALID_PAGE_RANGE1');
        isValid = false;
        break;
        // make sure that in each range of numbers the first number in the range is less then the second number
      } else if (rangeSplit[i].match(/^\d+\-\d+$/)) {
        const dashPos = rangeSplit[i].indexOf('-'),
          v1 = parseInt(rangeSplit[i].substring(0, dashPos)),
          v2 = parseInt(rangeSplit[i].substring(dashPos + 1));
        if (v1 >= v2) {
          // set error,set isValid to false & break out of loop
          this.setRangeError(rangeInput, 'SDE_STARTING_PAGE_CANNOT');
          isValid = false;
          break;
          // make sure the neither of the numbers in the page range are zero or greater than 9999
        } else if (v1 == 0 || v2 == 0 || v1 > 9999 || v2 > 9999) {
          // set error,set isValid to false & break out of loop
          this.setRangeError(rangeInput, 'SDE_INVALID_PAGE_RANGE1');
          isValid = false;
          break;
        }
        // make sure that any single page number isn't 0 or greater than 9999
      } else if (rangeSplit[i].match(/^\d+$/)) {
        if (parseInt(rangeSplit[i]) == 0 || parseInt(rangeSplit[i]) > 9999) {
          // set error,set isValid to false & break out of loop
          this.setRangeError(rangeInput, 'SDE_INVALID_PAGE_RANGE1');
          isValid = false;
          break;
        }
      }
    }
    return isValid;
  }

  setRangeError(input: any, error: string) {
    setTimeout(() => {
      this.input.selectedPagesError = error;
      input.setErrors({ customerror: true });
    }, 100);
  }

  handleEnterKeyPress(event: Event) {
    // prevent <Enter> form submission if not a button
    if ((event.target as HTMLElement).tagName.toLowerCase() !== 'button') {
      return false;
    }
    return;
  }

  ngOnDestroy() {
    this.cloudRepoSub.unsubscribe();
    this.xaccServiceLoginSub.unsubscribe();
  }
}

enum Occurrence {
  Hourly,
  Daily,
  Weekly,
}

enum StepStatus {
  Default, // default or initial settings
  Warning, // default settings changed by another step
  Valid, // valid settings
  Invalid, // invalid settings
  Revalidate, // invalid settings have been edited or reset
}

enum StepMode {
  Disabled,
  Edit,
  Inactive,
}

export interface Step {
  index: number;
  name: string;
  label: string;
  glyph: string;
  status: StepStatus;
  mode: StepMode;
  hidden?: boolean;
}

export interface CombinedServiceSettings {
  name: string;
  validations: Record<string, boolean>;
  settings: Parameter[];
}
